import React, { useEffect, useRef } from 'react';
import { IPerson, selectPersonLoggedIn } from '../../../store/slices/auth';
import {
  getNotificationsHistory,
  selectNotificationHistory,
  selectNotifications,
  markNotificationAsRead,
  selectHasMore,
} from '../../../store/slices/notifications';
import { useAppDispatch, useAppSelector } from '../../../store/store';
import { deleteByPersonIdandType } from '../../../services/indexedDB';
import { LimitNotificationParam } from '../../../enums/enums';
import NotificationItem from './NotificationItem';
import InfiniteScroll from 'react-infinite-scroll-component';
import styles from './NotificationDropDown.module.scss';
import { useTranslation } from '../../../hooks/useTranslations';

export default function NotificationDropDown() {
    const { translations } = useTranslation();
  const dispatch = useAppDispatch();
  const ref = useRef<HTMLInputElement | undefined>();
  const [open, setOpen] = React.useState(false);
  const hasMore = useAppSelector(selectHasMore);
  const notifications = useAppSelector(selectNotifications);
  const personLoggedIn = useAppSelector<IPerson>(selectPersonLoggedIn);
  const notificationsHistory = useAppSelector(selectNotificationHistory);
  const [paginationsParams, setPaginationsParams] = React.useState({
    limit: LimitNotificationParam,
    offset: 0,
    person_id: parseFloat(personLoggedIn?.person_external_id),
    only_unread: false,
  });

  const consultationNotifications = notifications.filter(
    (el: any) => el.type === 'consultation_notification'
  );

  // Check if the notificationsHistory is empty and the dropdown is open, then fetch the notificationsHistory
  useEffect(() => {
    if (open)
      dispatch(getNotificationsHistory({ ...paginationsParams, offset: 0, resetState: true }));
  }, [open, dispatch]);

  // Mark all unread notifications as read
  useEffect(() => {
    if (notificationsHistory.length > 0 && open) {
      const unReadNotifications = notificationsHistory
        .filter((el: any) => el.read === false)
        .map((el: any) => el.id);
      if (unReadNotifications.length > 0)
        dispatch(
          markNotificationAsRead({
            notification_ids: unReadNotifications.map((el: number) => ({ channel: 'I', id: el })),
          })
        );
    }
  }, [notificationsHistory, dispatch]);

  const handleOpen = () => {
    setOpen(!open);
    deleteByPersonIdandType(
      parseFloat(personLoggedIn.person_external_id),
      'consultation_notification'
    );
  };

  const loadMore = () => {
    setPaginationsParams((params) => {
      const updatedParams = {
        ...params,
        offset: notificationsHistory.length + LimitNotificationParam,
      };
      dispatch(getNotificationsHistory(updatedParams));
      return updatedParams;
    }); // Increment offset by limit}
  };

  // Close the dropdown when clicked outside the container
  useEffect(() => {
    const checkIfClickedOutside = (e: any) => {
      if (open && ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', checkIfClickedOutside);

    return () => document.removeEventListener('mousedown', checkIfClickedOutside);
  }, [open]);

  return (
    <div className={`${styles.notificationsWrapper}`} ref={ref as React.RefObject<HTMLDivElement>}>
      <div
        className={open ? `${styles.notificationBtn} ${styles.open}` : `${styles.notificationBtn}`}
        onClick={handleOpen}
      >
        {consultationNotifications.length > 0 && <i className={`${styles.redDot}`} />}
        {open ? <i className={styles.whiteBellIcon} /> : <i className={styles.bellIcon} />}
      </div>
      <div
        className={
          open
            ? `${styles.notificationDropDown} ${styles._visible}`
            : `${styles.notificationDropDown}`
        }
      >
        <div className={styles.header}>Notifications</div>
        <InfiniteScroll
          dataLength={notificationsHistory.length}
          next={loadMore}
          hasMore={hasMore}
          loader={
            <div className="skeleton">
              <div className="skeleton-left flex0">
                <div className="square circle"></div>
              </div>
              <div className="skeleton-right flex2">
                <div className="line h17 w40 m10"></div>
                <div className="line"></div>
                <div className="line h8 w50"></div>
              </div>
            </div>
          }
          endMessage={
            <div className="notification-empty-state">
              {translations("notifications_last_30_days")}
            </div>
          }
          height={450}
        >
          {notificationsHistory.map((notification: any, idx: number) => (
            <NotificationItem item={notification} key={idx} />
          ))}
        </InfiniteScroll>
      </div>
    </div>
  );
}
